// tslint:disable: no-unbound-method
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import format from 'date-fns/format';

import { Observable, of as observableOf } from "rxjs";
import { catchError, tap } from "rxjs/operators";

import { IBookingCollection } from "./model/api/bookings";
import {
    IUserPatchField,
    IUserPaymentMethods,
    User,
    UserMarketingProfile,
} from "./model/api/user";

import { ConfigureService } from "../configure/configure.service";
import { GenericService } from "./generic.service";

@Injectable()
export class UserService {
    marketingProfileOptIn = false;
    private readonly me = "/users/me";

    constructor(
        private readonly http: HttpClient,
        private readonly genericService: GenericService,
        private readonly config: ConfigureService
    ) {}

    getPaymentMethods(): Observable<IUserPaymentMethods> {
        return this.http
            .get(
                `${this.config.params.apiClientUri}${this.me}/payment-methods`,
                this.genericService.addAuthHeaders()
            )
            .pipe(catchError(this.genericService.handleError));
    }

    createPaymentMethod(
        paymentMethodID: string
    ): Observable<IUserPaymentMethods> {
        return this.http
            .post(
                `${this.config.params.apiClientUri}${this.me}/payment-methods`,
                {
                    data: [
                        {
                            type: "payment-methods",
                            id: null,
                            attributes: {
                                source: paymentMethodID,
                            },
                        },
                    ],
                },
                this.genericService.addAuthHeaders()
            )
            .pipe(catchError(this.genericService.handleError));
    }

    deletePaymentMethod(paymentMethodID: string): Observable<null> {
        return this.http
            .delete(
                `${this.config.params.apiClientUri}${this.me}/payment-methods/${paymentMethodID}`,
                this.genericService.addAuthHeaders()
            )
            .pipe(catchError(this.genericService.handleError));
    }

    /**
     * __example__
     * getBookings(1, 4, 'filters[sports]=football')
     *
     * @param endpoint string
     * @param page number
     * @param size number
     * @param filters string
     *
     * @returns Observable<IBookingCollection>
     */
    getBookings(
        page: number,
        size: number,
        filters = "",
        type?: "futureOnly"
    ): Observable<IBookingCollection> {
        let extra = "";
        if (type === "futureOnly") {
            extra = `&filter[starts]=${format(
              new Date(),
              "yyyy-MM-dd"
            )}&sort=slot.starts`;
        }

        return this.http
            .get(
                // tslint:disable-next-line:max-line-length
                `${this.config.params.apiClientUri}${this.me}/bookings?page[number]=${page}&page[size]=${size}&include=pitches,slots${filters}${extra}`,
                this.genericService.addAuthHeaders()
            )
            .pipe(catchError(this.genericService.handleError));
    }

    /**
     * __example__
     * patchUserData(user)
     *
     * @param user <User>
     *
     * @returns Observable<User>
     */
    patchUserData(user: User): Observable<User> {
        return this.http
            .patch(
                `${this.config.params.apiClientUri}${this.me}`,
                user,
                this.genericService.addAuthHeaders()
            )
            .pipe(catchError(this.genericService.handleError));
    }

    /**
     *
     * __example__
     * patchUserFields({ password: 'B$d$$ASSPass12', old_password: 'Wr0ngP$$s' })
     *
     * @param fields Array<IUserPatchField>
     *
     * @returns Observable<User>
     */
    patchUserFields(attributes: IUserPatchField): Observable<User> {
        return this.http
            .patch(
                `${this.config.params.apiClientUri}${this.me}`,
                {
                    data: {
                        type: "users",
                        id: "me",
                        attributes,
                    },
                },
                this.genericService.addAuthHeaders()
            )
            .pipe(catchError(this.genericService.handleError));
    }

    /**
     * __example__
     * getUserAny('favourite-sports')
     *
     * @param endPoint <string>
     *
     * @returns Observable<any>
     */
    getUserAny(endPoint: string): Observable<any> {
        return this.http
            .get(
                `${this.config.params.apiClientUri}${this.me}/${endPoint}`,
                this.genericService.addAuthHeaders()
            )
            .pipe(catchError(this.genericService.handleError));
    }

    getUserMarketingProfile(): Observable<any> {
        const url = `${this.config.params.apiClientUri}${this.me}/marketing-profile`;

        return this.http.get(url, this.genericService.addAuthHeaders()).pipe(
            // tslint:disable-next-line: no-void-expression
            tap((data: UserMarketingProfile) => this.validateOptIn(data)),
            catchError((_) => {
                this.marketingProfileOptIn = false;

                return observableOf(undefined);
            })
        );
    }

    getUserMarketingProfileFields(): Observable<any> {
        return this.http
            .get(
                `${this.config.params.apiClientUri}${this.me}/marketing-profile-fields`,
                this.genericService.addAuthHeaders()
            )
            .pipe(catchError(this.genericService.handleError));
    }

    setMarketPreferences(decision: boolean): Observable<any> {
        return this.http
            .patch(
                `${this.config.params.apiClientUri}${this.me}/marketing-profile`,
                {
                    data: {
                        type: "marketing-profile",
                        id: "me",
                        attributes: {
                            status: decision ? "subscribed" : "unsubscribed",
                        },
                    },
                },
                this.genericService.addAuthHeaders()
            )
            .pipe(
                tap((_) => (this.marketingProfileOptIn = decision)),
                catchError(this.genericService.handleError)
            );
    }

    setMarketPreferencesFields(decision: any): Observable<any> {
        return this.http
            .patch(
                `${this.config.params.apiClientUri}${this.me}/marketing-profile`,
                {
                    data: {
                        type: "marketing-profile",
                        id: "me",
                        attributes: {
                            providerData: decision,
                        },
                    },
                },
                this.genericService.addAuthHeaders()
            )
            .pipe(catchError(this.genericService.handleError));
    }

    /**
     * __example__
     * postUserAny('favourite-sports', data)
     *
     * @param endPoint <string>
     * @param data <object>
     *
     * @returns Observable<any>
     */
    postUserAny(endPoint: string, data: any): Observable<any> {
        return this.http
            .post(
                `${this.config.params.apiClientUri}${this.me}/${endPoint}`,
                data,
                this.genericService.addAuthHeaders()
            )
            .pipe(catchError(this.genericService.handleError));
    }

    /**
     * __example__
     * postUserAny('favourite-sports', data)
     *
     * @param endPoint <string>
     * @param data <object>
     *
     * @returns Observable<any>
     */
    deleteUserAny(endPoint: string, data: any): Observable<any> {
        return this.http
            .delete(
                `${this.config.params.apiClientUri}${this.me}/${endPoint}`,
                this.genericService.addAuthHeadersWithBody(data)
            )
            .pipe(catchError(this.genericService.handleError));
    }

    private validateOptIn(data: UserMarketingProfile): void {
        this.marketingProfileOptIn = data.data.attributes.optIn;
    }
}
